import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined, SupportAgentOutlined,
    ChatOutlined, EmailOutlined, PhoneOutlined,
    KeyboardArrowDownOutlined, KeyboardArrowUpOutlined,
    CheckCircleOutline, SearchOutlined, ArticleOutlined,
    WhatsApp,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    blue:    '#007AFF',
    orange:  '#FF9500',
};

const CONTACT_OPTIONS = [
    { label: 'Live Chat',    sub: 'Avg reply: 2 min',   icon: ChatOutlined,       color: B.purple, available: true,  action: 'chat'      },
    { label: 'WhatsApp',     sub: 'Quick support',       icon: WhatsApp,           color: '#25D366', available: true, action: 'whatsapp'  },
    { label: 'Email Us',     sub: 'Reply within 24hr',  icon: EmailOutlined,      color: B.blue,   available: true,  action: 'email'     },
    { label: 'Call Us',      sub: 'Mon–Fri · 8am–6pm',  icon: PhoneOutlined,      color: B.green,  available: false, action: 'call'      },
];

const FAQS = [
    { q: 'How do I fund my wallet?',          a: 'Go to Wallet → Add Money. Choose Bank Transfer, Card, USSD, or QR Code. Funds reflect instantly for most methods.' },
    { q: 'Why was my transaction declined?',   a: 'Transactions can be declined due to insufficient balance, incorrect PIN, network issues, or card controls. Check your card settings and try again.' },
    { q: 'How long do transfers take?',        a: 'Internal transfers are instant. Bank transfers typically settle within 10–30 minutes. Some banks may take up to 24 hours.' },
    { q: 'How is my credit score calculated?', a: 'Your score is based on repayment history, credit utilization, loan history, and account age. Improve it by repaying loans on time and keeping utilization low.' },
    { q: 'How do I freeze my virtual card?',   a: 'Go to Wallet → Card → Freeze Card. Your card is instantly blocked. You can unfreeze it at any time from the same screen.' },
    { q: 'Is my money insured?',               a: 'Yes. Deposits are insured by the NDIC up to ₦5,000,000 per depositor. NG Credit is licensed by the CBN.' },
    { q: 'How do I earn raffle tickets?',      a: 'Save money, repay loans on time, refer friends, pay bills, and maintain your daily check-in streak. Each action earns tickets for our monthly draw.' },
];

const CATEGORIES = [
    { label: 'Payments',    emoji: '💳', color: B.purple },
    { label: 'Loans',       emoji: '💰', color: B.green  },
    { label: 'Card',        emoji: '🔒', color: '#5856D6' },
    { label: 'Account',     emoji: '👤', color: B.blue   },
    { label: 'Rewards',     emoji: '🎁', color: B.orange },
    { label: 'Security',    emoji: '🛡️', color: B.red    },
];

const TICKET = {
    id: 'TKT-2847',
    status: 'resolved',
    subject: 'Transfer delay - GTBank',
    updated: '2 days ago',
};

const SupportPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [openFaq,    setOpenFaq]    = useState<number | null>(null);
    const [search,     setSearch]     = useState('');
    const [chatOpen,   setChatOpen]   = useState(false);
    const [message,    setMessage]    = useState('');
    const [sent,       setSent]       = useState(false);

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const filteredFaqs = FAQS.filter(f =>
        !search || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase())
    );

    const handleSend = () => {
        if (!message.trim()) return;
        setSent(true);
        setTimeout(() => { setSent(false); setChatOpen(false); setMessage(''); }, 2500);
    };

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '20px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate(-1)}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Help & Support
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <SupportAgentOutlined sx={{ color: B.green, fontSize: 20 }} />
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Contact options ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
                    {CONTACT_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                            <Box key={opt.label} onClick={() => opt.available && opt.action === 'chat' && setChatOpen(true)} sx={{
                                ...glassCard, p: 2, cursor: opt.available ? 'pointer' : 'default',
                                opacity: opt.available ? 1 : .5,
                                transition: 'all .18s',
                                '&:hover': opt.available ? { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(opt.color, .2)}` } : {},
                            }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: alpha(opt.color, .12), border: `1px solid ${alpha(opt.color, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.25 }}>
                                    <Icon sx={{ color: opt.color, fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{opt.label}</Typography>
                                <Box display="flex" alignItems="center" gap={.5} mt={.25}>
                                    {opt.available && <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 4px ${B.green}` }} />}
                                    <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{opt.sub}</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Live chat panel ── */}
                {chatOpen && (
                    <Box sx={{ ...glassCard, p: 2.5, mb: 2, border: `1px solid ${alpha(B.purple, .25)}` }}>
                        {sent ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CheckCircleOutline sx={{ color: B.green, fontSize: 40, mb: 1 }} />
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', mb: .5 }}>Message Sent!</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.78rem' }}>An agent will reply shortly</Typography>
                            </Box>
                        ) : (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: B.green, boxShadow: `0 0 6px ${B.green}` }} />
                                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>Live Chat</Typography>
                                    </Box>
                                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 600 }}>Agent online</Typography>
                                </Box>
                                <Box sx={{ background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, borderRadius: '14px', p: 1.5, mb: 1.5 }}>
                                    <Typography sx={{ color: textMid, fontSize: '.8rem' }}>
                                        👋 Hi! I'm your NG Credit support agent. How can I help you today?
                                    </Typography>
                                </Box>
                                <textarea
                                    placeholder="Type your message…"
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    rows={3}
                                    style={{
                                        width: '100%', background: 'transparent', border: `1px solid ${dividerC}`,
                                        borderRadius: '14px', padding: '12px', outline: 'none', resize: 'none',
                                        color: textPri, fontSize: '.85rem', fontFamily: "'DM Sans', system-ui, sans-serif",
                                        boxSizing: 'border-box',
                                    }}
                                />
                                <Button fullWidth onClick={handleSend} disabled={!message.trim()}
                                        sx={{
                                            mt: 1.25, py: 1.4, borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '.85rem',
                                            background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                            color: '#fff', boxShadow: `0 8px 24px ${alpha(B.purple, .35)}`,
                                            '&:disabled': { opacity: .35, boxShadow: 'none' },
                                        }}>
                                    Send Message
                                </Button>
                            </>
                        )}
                    </Box>
                )}

                {/* ── Open ticket ── */}
                <Box sx={{ ...glassCard, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: B.greenDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <CheckCircleOutline sx={{ color: B.green, fontSize: 20 }} />
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem' }}>{TICKET.subject}</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{TICKET.id} · Updated {TICKET.updated}</Typography>
                    </Box>
                    <Box sx={{ background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 8, px: 1, py: .35 }}>
                        <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700 }}>RESOLVED</Typography>
                    </Box>
                </Box>

                {/* ── Browse by category ── */}
                <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', mb: 2 }}>Browse Topics</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                        {CATEGORIES.map(cat => (
                            <Box key={cat.label} sx={{
                                p: 1.5, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                                background: alpha(cat.color, isDark ? .1 : .05),
                                border: `1px solid ${alpha(cat.color, .18)}`,
                                transition: 'all .18s',
                                '&:hover': { transform: 'translateY(-2px)', background: alpha(cat.color, .12) },
                            }}>
                                <Typography sx={{ fontSize: '1.3rem', mb: .5 }}>{cat.emoji}</Typography>
                                <Typography sx={{ color: textMid, fontSize: '.7rem', fontWeight: 600 }}>{cat.label}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── FAQ ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ArticleOutlined sx={{ color: B.purple, fontSize: 18 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>
                            Frequently Asked
                        </Typography>
                    </Box>

                    {/* search */}
                    <Box sx={{ px: 2.5, pb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${dividerC}`, borderRadius: '12px', px: 1.5, py: 1 }}>
                            <SearchOutlined sx={{ color: textFaint, fontSize: 16 }} />
                            <input
                                placeholder="Search FAQs…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: textPri, fontSize: '.82rem', fontFamily: "'DM Sans', system-ui, sans-serif" }}
                            />
                        </Box>
                    </Box>

                    {filteredFaqs.length === 0 ? (
                        <Box sx={{ px: 2.5, py: 2.5, textAlign: 'center' }}>
                            <Typography sx={{ color: textFaint, fontSize: '.82rem' }}>No results for "{search}"</Typography>
                        </Box>
                    ) : (
                        filteredFaqs.map((faq, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                                <Box sx={{ px: 2.5, py: 1.75, cursor: 'pointer' }} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                                        <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem', lineHeight: 1.4, flex: 1 }}>{faq.q}</Typography>
                                        {openFaq === i
                                            ? <KeyboardArrowUpOutlined sx={{ color: B.purple, fontSize: 20, flexShrink: 0, mt: .1 }} />
                                            : <KeyboardArrowDownOutlined sx={{ color: textFaint, fontSize: 20, flexShrink: 0, mt: .1 }} />
                                        }
                                    </Box>
                                    {openFaq === i && (
                                        <Typography sx={{ color: textMid, fontSize: '.8rem', lineHeight: 1.6, mt: 1.25, borderTop: `1px solid ${dividerC}`, pt: 1.25 }}>
                                            {faq.a}
                                        </Typography>
                                    )}
                                </Box>
                            </React.Fragment>
                        ))
                    )}
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default SupportPage;